<template>
  <div class="type-unit-plan">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />

    <local-version-banner v-if="resource.local_version" :content="resource.local_version" />

    <aside
      v-if="resource.lessons && resource.lessons.length > 0"
      aria-label="List of lessons"
      class="sidebar-lessons"
    >
      <div>
        <header>
          <h4 class="sidebar-subheading">Unit Plan</h4>
          <h3 class="sidebar-heading">{{ resource.name }}</h3>
          <p>
            <small>{{ description }}</small>
          </p>
        </header>
        <h5 class="sidebar-subheading">Lessons</h5>

        <lessons-list :lessons="resource.lessons" :active-lesson="resource.id" />
      </div>
    </aside>

    <div class="row tw-mt-4 sm:tw-mt-0">
      <div class="columns small-12 medium-12 large-9">
        <header>
          <div class="text-unit-plan sidebar-subheading">Unit Plan</div>
          <h1>{{ resource.name }}</h1>
        </header>

        <template v-if="resource.breadcrumbs">
          <t-breadcrumbs :items="resource.breadcrumbs" class="tw-mt-1" />
        </template>

        <div class="section section-actions flex flex-wrap align-middle">
          <download-button
            :files="resource.files"
            :resource-type="resource.type"
            :is-free="resource.is_free"
          />
          <action-buttons
            class="pull-right"
            :resource-id="resource.id"
            :event-params="resource?.event_params"
          />
        </div>

        <div class="callout callout-details">
          <publisher-link v-if="resource.publisher" :publisher="resource.publisher" />
          <div>
            <template v-if="resource.lessons && resource.lessons.length > 0">
              {{ resource.lessons.length }} x lessons
            </template>

            <template v-if="resource.year_levels_range">
              <span>|</span>
              Suitable for {{ $t('grade') }}s:
              <strong>{{ resource.year_levels_range }}</strong>
            </template>
          </div>
        </div>

        <section class="section section-content tw-prose tw-max-w-none">
          <p>
            <strong>{{ resource.excerpt }}</strong>
          </p>
          <div v-if="resource.content" class="description" v-html="resource.content"></div>
          <template v-if="loggedInUser">
            <p v-if="!canAccess" class="callout">Upgrade to view the Unit plan.</p>
          </template>
          <template v-else-if="!resource.is_free">
            <p class="callout">Login to view the Unit plan.</p>
          </template>
        </section>
        <template v-if="canAccess || resource.is_free">
          <section
            v-if="resource.lesson_objectives && resource.lesson_objectives.length > 0"
            class="section section-content"
          >
            <h3>Objectives</h3>
            <ul>
              <li v-for="item in resource.lesson_objectives" :key="item">
                {{ item }}
              </li>
            </ul>
          </section>

          <section v-if="resource.description" class="section section-content">
            <h3>Preparing for Learning</h3>
            <div class="description" v-html="resource.description"></div>
          </section>

          <section v-if="isAuthenticated && resource.files" class="section section-download">
            <h3>Select a download</h3>
            <div class="box no-padding">
              <download-link
                :files="resource.files"
                :resource-type="resource.type"
                :is-free="resource.is_free"
                :event-params="resource?.event_params"
                position="Content | Select a download"
                click-type="Text Link"
                click-text=""
              />
            </div>
          </section>

          <section
            v-if="resource.lessons && resource.lessons.length > 0"
            class="section section-lessons-list"
          >
            <h3>Lessons</h3>
            <lessons-list :lessons="resource.lessons" layout="full" />
          </section>
        </template>
        <section
          v-if="resource.curriculums && resource.curriculums.length > 0"
          class="section section-curriculum"
        >
          <h3>Curriculum</h3>
          <curriculum-list :curriculums="resource.curriculums" />
        </section>

        <section v-if="resource.author" class="section section-author">
          <h3>Author</h3>

          <div class="flex">
            <figure v-if="resource.author && resource.author.photo">
              <img
                :src="resource.author.photo"
                class="circle"
                :alt="'Photo of ' + resource.author.display_name"
                height="42"
                width="42"
              />
            </figure>
            <span>{{ resource.author.display_name }}</span>
          </div>
        </section>

        <section v-if="resource.publisher" class="section section-publisher">
          <div class="flex">
            <figure v-if="resource.publisher && resource.publisher.logo">
              <img
                :src="resource.publisher.logo_square"
                class="circle"
                height="42"
                width="42"
                :alt="resource.publisher.name"
              />
            </figure>
            <div>
              <h3>{{ resource.publisher.name }}</h3>
              <p>{{ resource.publisher.excerpt }}</p>
            </div>
          </div>
        </section>

        <tag-list
          :file-formats="resource.file_formats"
          :tags="resource.tags"
          :year-levels="resource.year_levels"
          :country="resource.country"
        />
      </div>

      <aside class="columns small-12 medium-12 large-3" aria-label="Related resources">
        <div>
          <figure v-if="!resource.header_bg_image && resource.media_thumbnail">
            <responsive-image :image="resource.media_thumbnail" alt="Unit Plan title image" />
          </figure>

          <div class="hidden-small">
            <h4 class="text-center">Included resources:</h4>

            <ul
              class="row block-grid block-grid-cards small-up-2 medium-up-2 large-up-1 scroll-container"
            >
              <li v-for="item in resource.resources" :key="item.id">
                <card v-if="item && item.link" type="browse" :item="item" :url="item.link" />
              </li>
            </ul>
          </div>
        </div>
      </aside>
      <comment-section
        class="columns small-12 section-comments"
        :resource-id="resource.id"
        :comment-count="resource.comments"
        :resource-updates="resource.updates"
        :event-params="resource?.event_params"
      />
    </div>
    <country-switcher :link="resource.link" :linked="resource.linked" :country="resource.country" />
  </div>
</template>

<script>
/* A single unit plan
 *
 * @parent:
 * @requests: /public/v1/resource
 * @events:
 * @props:
 * @components: ActionButtons, Card, TagList, CommentSection, CurriculumList, DownloadButton, DownloadLink, ImageGallery, ResourcesList
 * @methods:
 */

import { mapGetters } from 'vuex';

import ActionButtons from '~/components/resource/ActionButtons.vue';
import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import Card from '~/components/card/Card.vue';
import CommentSection from '~/components/comment/CommentSection.vue';
import DownloadButton from '~/components/resource/DownloadButton.vue';
import DownloadLink from '~/components/resource/DownloadLink.vue';
import LessonsList from '~/components/resource/LessonsList.vue';
import TagList from '~/components/resource/TagList.vue';
import CurriculumList from '~/components/resource/CurriculumList.vue';
import ResponsiveImage from '~/components/ResponsiveImage.vue';
import PublisherLink from '~/components/PublisherLink.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';

export default {
  name: 'UnitPlan',
  components: {
    ActionButtons,
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    TBreadcrumbs,
    Card,
    CommentSection,
    DownloadButton,
    DownloadLink,
    LessonsList,
    LocalVersionBanner: () => import('~/components/resource/LocalVersionBanner.vue'),
    TagList,
    CurriculumList,
    ResponsiveImage,
    PublisherLink,
    CountrySwitcher,
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['unit-plan'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Unit plan',
      description: 'A unit plan from Teach Starter',
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  head() {
    return {
      title: this.title,
      meta: this.$metaPropertiesResource(this.resource),
      link: this.$hrefLangsResource(
        this.resource.linked,
        this.resource.link,
        this.resource.country
      ),
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'loggedInUser', 'resource']),
    canAccess() {
      return this.loggedInUser
        ? this.resource.is_free ||
            this.loggedInUser.capabilities.includes('access_product_units_lessons')
        : false;
    },
  },
  mounted() {
    if (this.resource?.event_params) {
      this.$mzAnalyticsApi({
        event: 'pageView',
        name: 'unit_plan',
        productArea: 'unit_plan',
        entityType: 'unit_plan',
        entityId: this.resource.id,
      });
    }
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/sections/_resource-page.scss';
@import '~/assets/scss/sections/_lesson-plan-page.scss';
</style>
